import {
  calculateGapGuides,
  computeAlignmentAnalysis,
  guidesFromAnalysis,
  type AlignmentAnalysis,
  type AlignmentBox,
  createAlignmentBoxFromLayout,
  translateAlignmentBox,
} from './alignmentMath';
import {
  GUIDE_VISIBILITY_THRESHOLD_PX,
  resolveFineMoveMagneticSnap,
  type MagneticSnapDebug,
} from './magneticSnap';
import type { CanvasSectionBlock } from '../sectionRegistry';
import { resolveGapAlignmentBoxFromLayer } from './editorBounds';
import type { AlignmentGuides } from './types';
import type { FineMoveInteractionState, FineMoveItem } from './transformEngineTypes';
import { clampFreePositionOffset, type ResponsiveBlockTransformStyle } from './transformStyle';
import type { LayoutSnapBlock } from './snap';

export type DragAxisLock = 'x' | 'y' | null;

/** Hold Shift while dragging to lock the axis with less movement (keep a settled axis fixed). */
export function resolveDragAxisConstrainedDelta(
  deltaXPx: number,
  deltaYPx: number,
  shiftKey: boolean
): { deltaXPx: number; deltaYPx: number; axisLock: DragAxisLock } {
  if (!shiftKey) {
    return { deltaXPx, deltaYPx, axisLock: null };
  }

  const absX = Math.abs(deltaXPx);
  const absY = Math.abs(deltaYPx);
  if (absX > absY) {
    return { deltaXPx, deltaYPx: 0, axisLock: 'y' };
  }
  if (absY > absX) {
    return { deltaXPx: 0, deltaYPx, axisLock: 'x' };
  }
  if (absX === 0 && absY === 0) {
    return { deltaXPx, deltaYPx, axisLock: null };
  }
  // Equal displacement: default to horizontal travel (lock vertical).
  return { deltaXPx, deltaYPx: 0, axisLock: 'y' };
}

export type DragModifierKeys = {
  altKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
};

/** Alt without Ctrl/Meta — disables magnetic snap while dragging. */
export function isDragSnapDisabled(keys: DragModifierKeys): boolean {
  return keys.altKey && !keys.ctrlKey && !keys.metaKey;
}

/** Ctrl+Alt (or Cmd+Alt) at drag start — duplicate block and drag the copy. */
export function isDragDuplicateModifier(keys: DragModifierKeys): boolean {
  return keys.altKey && (keys.ctrlKey || keys.metaKey);
}

function combineAlignmentBoxes(boxes: AlignmentBox[], fallback: AlignmentBox): AlignmentBox {
  const finiteBoxes = boxes.filter((box) =>
    [box.left, box.top, box.right, box.bottom, box.width, box.height].every(Number.isFinite)
  );
  if (finiteBoxes.length === 0) return fallback;

  const left = Math.min(...finiteBoxes.map((box) => box.left));
  const top = Math.min(...finiteBoxes.map((box) => box.top));
  const right = Math.max(...finiteBoxes.map((box) => box.right));
  const bottom = Math.max(...finiteBoxes.map((box) => box.bottom));
  const width = Math.max(1, right - left);
  const height = Math.max(1, bottom - top);

  return {
    id: fallback.id,
    left,
    top,
    width,
    height,
    right,
    bottom,
    centerX: left + width / 2,
    centerY: top + height / 2,
  };
}

function createFineMoveRawPreviewBox(
  item: FineMoveItem,
  colWidth: number,
  rowHeight: number,
  deltaXPx: number,
  deltaYPx: number
) {
  const startBox = item.startFrameBox ?? item.startEditorBox;
  if (startBox) {
    return translateAlignmentBox(startBox, deltaXPx, deltaYPx);
  }
  return createAlignmentBoxFromLayout(item.startLayout, colWidth, rowHeight, {
    id: item.blockId,
    offsetXPx: item.startOffsetXPx + deltaXPx,
    offsetYPx: item.startOffsetYPx + deltaYPx,
    widthPx: item.blockWidth,
    heightPx: item.blockHeight,
  });
}

export function getFineMoveItems(interaction: FineMoveInteractionState): FineMoveItem[] {
  if (interaction.groupItems && interaction.groupItems.length > 0) {
    return interaction.groupItems;
  }
  return [
    {
      blockId: interaction.blockId,
      startLayout: interaction.startLayout,
      startStyle: interaction.startStyle,
      startOffsetXPx: interaction.startOffsetXPx,
      startOffsetYPx: interaction.startOffsetYPx,
      startEditorBox: interaction.startFrameBox ?? interaction.startEditorBox,
      startFrameBox: interaction.startFrameBox ?? interaction.startEditorBox,
      blockWidth: interaction.blockWidth,
      blockHeight: interaction.blockHeight,
      nextStyle: interaction.nextStyle,
    },
  ];
}

export function resolveFineMovePreview({
  interaction,
  clientX,
  clientY,
  snapDisabled,
  shiftKey = false,
  referenceBoxes,
  blockKindsById,
  movingBoxSource = 'layout',
  readTransform,
  writeTransform,
}: {
  interaction: FineMoveInteractionState;
  clientX: number;
  clientY: number;
  snapDisabled: boolean;
  shiftKey?: boolean;
  referenceBoxes: AlignmentBox[];
  blockKindsById?: Record<string, string>;
  movingBoxSource?: 'content' | 'frame' | 'text-paragraph' | 'layout';
  readTransform: (style: Record<string, unknown>) => ResponsiveBlockTransformStyle;
  writeTransform: (
    style: Record<string, unknown>,
    transform: Record<string, number | null>
  ) => Record<string, unknown>;
}): {
  guides: AlignmentGuides;
  updates: Array<{ blockId: string; nextStyle: Record<string, unknown> }>;
  movingBox?: AlignmentBox;
  analysis?: AlignmentAnalysis;
  magneticSnapDebug?: MagneticSnapDebug;
  axisLock?: DragAxisLock;
} {
  const placementNudgeXPx = interaction.placementNudgeXPx ?? 0;
  const placementNudgeYPx = interaction.placementNudgeYPx ?? 0;
  const pointerDeltaXPx = clientX - interaction.startClientX + placementNudgeXPx;
  const pointerDeltaYPx = clientY - interaction.startClientY + placementNudgeYPx;
  const {
    deltaXPx,
    deltaYPx,
    axisLock,
  } = resolveDragAxisConstrainedDelta(pointerDeltaXPx, pointerDeltaYPx, shiftKey);
  const groupItems = getFineMoveItems(interaction);
  const primaryItem =
    groupItems.find((item) => item.blockId === interaction.blockId) ?? groupItems[0];

  const rawPrimaryBox = createFineMoveRawPreviewBox(
    primaryItem,
    interaction.colWidth,
    interaction.rowHeight,
    deltaXPx,
    deltaYPx
  );
  const rawMovingBox =
    groupItems.length > 1
      ? combineAlignmentBoxes(
          groupItems.map((item) =>
            createFineMoveRawPreviewBox(
              item,
              interaction.colWidth,
              interaction.rowHeight,
              deltaXPx,
              deltaYPx
            )
          ),
          rawPrimaryBox
        )
      : rawPrimaryBox;

  const sectionWidth = interaction.colWidth * interaction.totalColumns;
  const sectionHeight = interaction.rowHeight * interaction.maxRows;

  const previewAnalysis = computeAlignmentAnalysis({
    movingBox: rawMovingBox,
    referenceBoxes,
    sectionWidth,
    sectionHeight,
    threshold: GUIDE_VISIBILITY_THRESHOLD_PX,
    movingBlockId: interaction.blockId,
    movingBoxSource,
    blockKindsById,
  });

  const magnetic = resolveFineMoveMagneticSnap({
    rawPreviewBox: rawMovingBox,
    activeXGuide: previewAnalysis.activeXGuide,
    activeYGuide: previewAnalysis.activeYGuide,
    referenceBoxes,
    sectionWidth,
    movingBlockId: interaction.blockId,
    snapDisabled,
    verticalAlignmentPriority: previewAnalysis.verticalAlignmentDebug?.activeYGuide?.priority,
    rowPeerIds: previewAnalysis.rowPeerIds,
  });

  const magnetisedMovingBox = magnetic.magnetisedPreviewBox;
  const snapOffsetXDelta = axisLock === 'x' ? 0 : magnetic.appliedDelta.x;
  const snapOffsetYDelta = axisLock === 'y' ? 0 : magnetic.appliedDelta.y;

  const gapResult = calculateGapGuides(
    magnetisedMovingBox,
    referenceBoxes,
    sectionWidth,
    interaction.blockId,
    movingBoxSource
  );

  const analysis: AlignmentAnalysis = {
    ...previewAnalysis,
    movingBox: magnetisedMovingBox,
    gapGuides: gapResult.guides,
    gapDebug: gapResult.gapDebug,
    spacingEqual: gapResult.equal,
    spacingEqualX: gapResult.equalX,
    spacingEqualY: gapResult.equalY,
    spacingEqualAll: gapResult.equalAll,
    candidateRejections: gapResult.candidateRejections,
    rowPeerIds: Array.from(gapResult.rowPeerIds),
    explicitRowMode: gapResult.explicitRowMode,
    explicitColumnMode: gapResult.explicitColumnMode,
    verticalSpacingDebug: gapResult.verticalSpacingDebug,
  };

  const guides = guidesFromAnalysis(analysis, magnetisedMovingBox);

  const rawDeltaX = deltaXPx + snapOffsetXDelta;
  const rawDeltaY = deltaYPx + snapOffsetYDelta;
  let appliedDeltaX = rawDeltaX;
  let appliedDeltaY = rawDeltaY;
  if (groupItems.length > 1) {
    const clampedPrimaryX = clampFreePositionOffset(
      primaryItem.startOffsetXPx + rawDeltaX,
      primaryItem.blockWidth
    );
    const clampedPrimaryY = clampFreePositionOffset(
      primaryItem.startOffsetYPx + rawDeltaY,
      primaryItem.blockHeight
    );
    appliedDeltaX = clampedPrimaryX - primaryItem.startOffsetXPx;
    appliedDeltaY = clampedPrimaryY - primaryItem.startOffsetYPx;
  }

  const updates = groupItems.map((item) => {
    const itemOffsetXPx = item.startOffsetXPx + appliedDeltaX;
    const itemOffsetYPx = item.startOffsetYPx + appliedDeltaY;
    const currentTransform = readTransform(item.startStyle);
    const nextTransform = {
      ...currentTransform,
      offsetX: 0,
      offsetY: 0,
      offsetXPx:
        groupItems.length > 1
          ? itemOffsetXPx
          : clampFreePositionOffset(itemOffsetXPx, item.blockWidth),
      offsetYPx:
        groupItems.length > 1
          ? itemOffsetYPx
          : clampFreePositionOffset(itemOffsetYPx, item.blockHeight),
    };

    return {
      blockId: item.blockId,
      nextStyle: writeTransform(item.startStyle, nextTransform),
    };
  });

  return {
    guides,
    updates,
    movingBox: magnetisedMovingBox,
    analysis,
    magneticSnapDebug: magnetic.debug,
    axisLock,
  };
}

/** Live DOM boxes for gap math — same content/active visual source as selection overlays. */
export function buildFineMoveLiveGapBoxes({
  layer,
  blocks,
  referenceBlocks,
  movingBlockId,
}: {
  layer: HTMLElement;
  blocks: CanvasSectionBlock[];
  referenceBlocks: LayoutSnapBlock[];
  movingBlockId: string;
}): {
  movingBox: AlignmentBox | null;
  referenceBoxes: AlignmentBox[];
} {
  const movingBlock = blocks.find((candidate) => candidate.id === movingBlockId);
  const referenceBoxes = referenceBlocks
    .map((reference) => blocks.find((candidate) => candidate.id === reference.id))
    .filter((block): block is CanvasSectionBlock => Boolean(block))
    .map((block) => resolveGapAlignmentBoxFromLayer(block, layer))
    .filter((box): box is AlignmentBox => Boolean(box));

  const movingBox = movingBlock ? resolveGapAlignmentBoxFromLayer(movingBlock, layer) : null;

  return { movingBox, referenceBoxes };
}

/** Re-run gap/spacing guides on the live DOM moving box after preview styles are applied. */
export function reconcileFineMoveGuidesWithLiveMovingBox({
  analysis,
  liveMovingBox,
  referenceBoxes,
  sectionWidth,
  movingBlockId,
  movingBoxSource = 'content',
}: {
  analysis: AlignmentAnalysis;
  liveMovingBox: AlignmentBox;
  referenceBoxes: AlignmentBox[];
  sectionWidth: number;
  movingBlockId?: string;
  movingBoxSource?: 'content' | 'frame' | 'text-paragraph' | 'layout';
}): {
  guides: AlignmentGuides;
  analysis: AlignmentAnalysis;
  movingBox: AlignmentBox;
} {
  const gapResult = calculateGapGuides(
    liveMovingBox,
    referenceBoxes,
    sectionWidth,
    movingBlockId,
    movingBoxSource
  );

  const reconciledAnalysis: AlignmentAnalysis = {
    ...analysis,
    movingBox: liveMovingBox,
    gapGuides: gapResult.guides,
    gapDebug: gapResult.gapDebug,
    spacingEqual: gapResult.equal,
    spacingEqualX: gapResult.equalX,
    spacingEqualY: gapResult.equalY,
    spacingEqualAll: gapResult.equalAll,
    candidateRejections: gapResult.candidateRejections,
    rowPeerIds: Array.from(gapResult.rowPeerIds),
    explicitRowMode: gapResult.explicitRowMode,
    explicitColumnMode: gapResult.explicitColumnMode,
    verticalSpacingDebug: gapResult.verticalSpacingDebug,
  };

  return {
    guides: guidesFromAnalysis(reconciledAnalysis, liveMovingBox),
    analysis: reconciledAnalysis,
    movingBox: liveMovingBox,
  };
}

export function computeFineMoveGroupCenterClient(
  groupItems: FineMoveItem[],
  colWidth: number,
  rowHeight: number,
  layerRect: DOMRect
) {
  const boxes = groupItems.map((item) =>
    createFineMoveRawPreviewBox(item, colWidth, rowHeight, 0, 0)
  );
  const combined = combineAlignmentBoxes(boxes, boxes[0]);
  return {
    x: layerRect.left + combined.centerX,
    y: layerRect.top + combined.centerY,
  };
}
